import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Media from 'react-media';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';

import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import * as toastsActions from '@zola-helpers/client/dist/es/redux/toasts/toastsActions';
import { useAppDispatch } from '@/store';
import TileSwatchList from '../../components/common/shop/ProductTile/TileSwatchList';
import PreviewCard from '../../components/common/modal/PersonalizationModal/components/PreviewCard';
import gift from '../../assets/images/icons/gift.svg';
import Attributes, {
  buildSkuAttributeMap,
  buildLookAttributeMap,
  getStaticAttributes,
} from './Attributes';
import TabPanel, { AccordionPanel } from './TabPanel';

/**
 * In component public registry repo, we have the same function. We duplicate it in web-registry
 * because component-public-registry is not optimized for code splitting. Import it directly can cause
 * unnecessary bundle size increase. This is a registry specific until function, so we won't move it to
 * zola-helper.
 */
import formatPromoMessage from '../../util/promoMessageFormatter';
import {
  renderActionButton,
  renderQuantity,
  updateProductLook,
  MSRPToolTip,
  handleAttributeChange,
} from './ProductOverviewCore';
import {
  findSwatchByProductLookId,
  formatExpShippingDate,
  hidePriceMatchingCopy,
  getShippingText,
  getPersonalizedArrivalCopy,
  returnHolidayShippingMessage,
} from './helpers';
import { formatPrice } from '../../util/currencyFormatter';
import { getProductPrices } from '../../util/productPrice';
import { removePersonalizationFormData } from '../../actions/PersonalizationActions';
import { useAddToRegistryFromPDP } from './useAddToRegistryFromPDP';

const PayInFourMessage = dynamic(
  () =>
    import('@zola/zola-ui/src/components/Cart/PayPal/index').then(
      (module) => module.default.PayInFourMessage
    ),
  {
    ssr: false,
  }
);

const ShippingProductText = ({
  shipDates,
  productData,
  deliverySurcharge,
  isPersonalizable,
  activeSku,
}) => {
  if (!shipDates) return null;
  if (!shipDates.freeShippingMin) return null;
  const { freeShippingMax, freeShippingMin, expShippingMax, expShippingMin } = shipDates;

  const renderHolidayShipping = () => {
    const holidayShippingMessage = returnHolidayShippingMessage(
      productData,
      freeShippingMin,
      expShippingMax,
      activeSku
    );

    return holidayShippingMessage ? (
      <>
        <div className="shipping-text holiday-arrival-question">
          <img src={gift} alt="" className="holiday-arrival-question__icon" /> Want it by 12/24?
        </div>
        <div className="shipping-text holiday-arrival-suggestion">{holidayShippingMessage}</div>
      </>
    ) : null;
  };

  return (
    <div className="shipping-text_container">
      <div className="shipping-text arrival">Estimated Arrival</div>
      {isPersonalizable && <div className="shipping-text">{getPersonalizedArrivalCopy()}</div>}
      <div className="shipping-text">
        {getShippingText(productData.ship_method, deliverySurcharge)}: {freeShippingMin} -{' '}
        {freeShippingMax}
      </div>
      {expShippingMax && expShippingMin ? (
        <div className="shipping-text">{formatExpShippingDate(expShippingMin, expShippingMax)}</div>
      ) : null}
      {renderHolidayShipping()}
    </div>
  );
};

const ProductOverview = (props) => {
  const {
    savedPersonalization,
    openPersonalizationModal,
    isPersonalizationRequired,
    productData,
    activeProductLook,
    setActiveProductLook,
    shipDates,
    productAttributes,
    setProductAttributes,
    quantity,
    setQuantity,
    setActiveSku,
    activeSku,
    handleAddToCart,
    userContext,
  } = props;
  if (!productData || !activeProductLook) return null;
  const dispatch = useAppDispatch();
  const handleAddToRegistry = useAddToRegistryFromPDP({
    productData,
    activeSku,
    quantity,
    userContext,
  });

  const [skuAttributeMap] = useState(buildSkuAttributeMap(productData.product_look_views));
  const [lookAttributeMap] = useState(buildLookAttributeMap(productData.attributes));
  const staticAttributes = getStaticAttributes(productData.product_look_views, lookAttributeMap);
  const productId = productData.id;
  const brandName = productData.brand.name;
  const brandKey = productData.brand.key;
  const productName = productData.display_name;
  const activeSwatch = findSwatchByProductLookId(activeProductLook.id, productData.swatches);
  const productPrices = getProductPrices(activeProductLook, activeSku);
  const { actions } = !activeSku ? activeProductLook : activeSku;

  // if user has a registry then wait for response and clear customizations
  // otherwise user will be reidrected to the onboarding flow
  const onRegistryAdd = () => {
    const promise = handleAddToRegistry();
    if (promise) {
      promise.then(() => {
        dispatch(removePersonalizationFormData());
      });
    }
  };

  const actionHandlers = {
    ADD_TO_REGISTRY: onRegistryAdd,
    ADD_TO_CART: handleAddToCart,
  };
  const productIsPersonalizable = productData && productData.personalizable;

  const setters = {
    setActiveProductLook,
    setProductAttributes,
    setActiveSku,
  };

  useEffect(() => {
    // clear saved personalization if user switches to different product
    dispatch(removePersonalizationFormData());
  }, [activeSku]);

  const onSwatchChange = (productLookId) => {
    updateProductLook(productLookId, productData, setters);
  };

  const onAttributeChange = handleAttributeChange(
    productData,
    productAttributes,
    activeProductLook,
    setters
  );

  const findCommonPromo = (skuPreviews) => {
    const promos = skuPreviews.map((n) => n.promo_display_view);
    const results = uniqBy(promos, 'discount_id');
    return results.length === 1 && results[0];
  };

  // set promo message for template
  const renderPromoMessage = () => {
    let promo;
    if (activeSku) {
      promo = activeSku.promo_display_view;
    } else if (activeProductLook.sku_previews) {
      promo = findCommonPromo(activeProductLook.sku_previews);
    }

    if (!promo) {
      return null;
    }

    if (promo.discount_type === 'COMPLETION') {
      return (
        <div className="pdp-promo-detail">
          <b>{promo.discount_amount}% Off</b> With Post-Wedding Discount{' '}
          <LinkV2
            variant="secondary"
            className="pdp-promo-detail__link"
            target="_blank"
            rel="noopener noreferrer"
            href="/faq/115002837952-how-do-we-use-our-20-post-wedding-discount-"
          >
            Details
          </LinkV2>
        </div>
      );
    }

    const promoMessage = formatPromoMessage(promo);
    return (
      <div className="pdp-promo-detail">
        {promoMessage} <b className="pdp-promo-detail__code text-uppercase">{promo.code}</b>{' '}
        <LinkV2
          variant="secondary"
          className="pdp-promo-detail__link"
          target="_blank"
          rel="noopener noreferrer"
          href={`/discount-terms/${promo.discount_id}`}
        >
          Details
        </LinkV2>
      </div>
    );
  };

  // Set personalization button to open custimozation modal if personalization available for sku.
  const renderPersonalizationLink = (personalizationSurcharge) => (
    <div className="pdp-personalization-cta">
      <LinkV2 arrow href="" onClick={openPersonalizationModal}>
        Personalize It (+{personalizationSurcharge}/Ea.)
      </LinkV2>
    </div>
  );

  const onPersonalizationsRemove = (e) => {
    e.preventDefault();
    dispatch(removePersonalizationFormData());
    const headline = <span>Personalization Removed</span>;
    dispatch(toastsActions.negative({ headline }));
  };

  const renderPersonalizationPreview = () => (
    <PreviewCard
      personalizations={savedPersonalization}
      onRemove={onPersonalizationsRemove}
      onEdit={openPersonalizationModal}
    />
  );

  const renderPersonalizeCTA = () => {
    const isSkuPersonalizable = activeSku && activeSku.personalizable;
    if (isSkuPersonalizable) {
      const personalizationSurcharge = formatPrice(activeSku.personalization_surcharge_cents);
      return savedPersonalization
        ? renderPersonalizationPreview()
        : !activeSku.must_be_personalized && renderPersonalizationLink(personalizationSurcharge);
    }
    return (
      <div className="pdp-no-personalization-cta">Personalization available for select styles</div>
    );
  };

  const renderPersonalizationAction = () => (
    <ButtonV2
      onClick={openPersonalizationModal}
      variant="primary"
      type="submit"
      className="pdp-add-button personalization-button"
      compact
    >
      <div>Personalize</div>
    </ButtonV2>
  );

  const renderStockMessageText = (text) => {
    const backorderRegex = /^(backordered)/gi;
    // on PDP stockMessage shoould show different copy than other placces that use copy from API
    const backorderedStockCopy = 'Backordered: Availability date pending from brand';
    const stockMessageText = backorderRegex.test(text) ? backorderedStockCopy : text;
    return <div className="stock-message-text">{stockMessageText}</div>;
  };

  return (
    <div className="product-overview_container">
      <div className="product-details_container">
        <div className="product-details">
          <div className="brand-product-names-nonmobile">
            <LinkV2
              variant="primary"
              noUnderline
              href={`/shop/brand/${brandKey}`}
              className="brand-name"
            >
              {brandName}
            </LinkV2>
            <h1 className="product-name">{productName}</h1>
            <div
              className="product-rating"
              data-bv-product-id={productId}
              data-bv-show="rating_summary"
            />
          </div>
          {productPrices.msrp ? (
            <div className="msrp-price">
              MSRP
              <span data-tooltip={MSRPToolTip}>
                <i className="zolaicon-info" />
              </span>{' '}
              {productPrices.msrp}
            </div>
          ) : null}
          {productPrices.sale ? (
            <div className="sale-price">
              Was&nbsp;<span>{productPrices.sale}</span>
            </div>
          ) : null}
          {productPrices.discount ? (
            <div className="sale-price">
              Was&nbsp;<span>{productPrices.discount}</span>
            </div>
          ) : null}
          <div className="zola-price">{productPrices.zolaPrice}</div>
          {renderPromoMessage()}
          {hidePriceMatchingCopy(activeProductLook, activeSku, productData.type) ? null : (
            <span className="price-matching-text">
              <LinkV2
                variant="primary"
                noUnderline
                href="/faq/115002838511-does-zola-offer-price-matching-what-is-zola-s-price-matching-policy-"
                className="base-text"
              >
                Price Matching Available
              </LinkV2>
            </span>
          )}
          {!productData.cash_fund && activeSku && typeof activeSku.price_cents === 'number' && (
            <PayInFourMessage amountCents={activeSku.price_cents * parseInt(quantity, 10)} />
          )}
          {staticAttributes.length > 0 &&
            staticAttributes.map((attribute) => (
              <div key={`${attribute[0]}-${attribute[1]}`} className="active-color">
                <span>{attribute[0]}:</span> {attribute[1]}
              </div>
            ))}
          {activeSwatch ? (
            <div className="active-color">
              <span>{productData.swatch_group_label}:</span> {activeSwatch.label}
            </div>
          ) : null}
          <TileSwatchList
            activeProductLookId={activeProductLook.id}
            limit={99}
            swatches={productData.swatches}
            onSwatchChange={onSwatchChange}
          />

          <Attributes
            productData={productData}
            productAttributes={productAttributes}
            setProductAttributes={setProductAttributes}
            activeProductLook={activeProductLook}
            skuAttributeMap={skuAttributeMap}
            lookAttributeMap={lookAttributeMap}
            onAttributeChange={onAttributeChange}
          />
          {productIsPersonalizable && renderPersonalizeCTA()}
        </div>
        <div className="user-input-form">
          {renderQuantity(productData, quantity, setQuantity)}
          <Media query="(min-width: 481px)">
            <div className="action-buttons">
              {isPersonalizationRequired
                ? renderPersonalizationAction()
                : actions.map((action) => renderActionButton(action, actionHandlers[action.key]))}
            </div>
          </Media>
          {activeSku && activeSku.stock_message && renderStockMessageText(activeSku.stock_message)}
          {isPersonalizationRequired && (
            <div className="porduct-overview__personalization-message">
              This item must be personalized before you add it to cart or registry.
            </div>
          )}
          {!isPersonalizationRequired && activeSku && activeSku.free_shipping ? (
            <LinkV2 variant="primary" noUnderline href="/shipping" className="free-shipping-text">
              <span className="fast-shipping-icon" />
              Free Shipping & Returns
            </LinkV2>
          ) : null}
          {!!activeSku?.delivery_surcharge_cents && (
            <LinkV2 variant="primary" noUnderline href="/shipping" className="free-shipping-text">
              Delivery Surcharge: {formatPrice(activeSku.delivery_surcharge_cents)}
            </LinkV2>
          )}
          {activeSku && (
            <ShippingProductText
              deliverySurcharge={activeSku.delivery_surcharge_cents}
              shipDates={shipDates}
              productData={productData}
              isPersonalizable={productIsPersonalizable}
              activeSku={activeSku}
            />
          )}
        </div>
        {/* Mobile description/details/shipping info */}
        <AccordionPanel
          productData={productData}
          activeProductLook={activeProductLook}
          activeSku={activeSku}
          shipDates={shipDates}
        />
      </div>
      {/* Desktop description/details/shipping info */}
      <TabPanel
        productData={productData}
        activeProductLook={activeProductLook}
        activeSku={activeSku}
        shipDates={shipDates}
        productAttributes={productAttributes}
        type="desktop"
      />
    </div>
  );
};

ShippingProductText.propTypes = {
  shipDates: PropTypes.shape({}),
  productData: PropTypes.shape({}), // fetchProductByKey payload
  deliverySurcharge: PropTypes.number,
  isPersonalizable: PropTypes.bool,
  activeSku: PropTypes.shape({}),
};

ProductOverview.propTypes = {
  productData: PropTypes.shape({}), // fetchProductByKey payload
  activeProductLook: PropTypes.shape({}), // fetchProductByKey payload > product_look_views
  setActiveProductLook: PropTypes.func.isRequired,
  shipDates: PropTypes.shape({}),
  productAttributes: PropTypes.shape({}),
  setProductAttributes: PropTypes.func.isRequired,
  quantity: PropTypes.number,
  setActiveSku: PropTypes.func.isRequired,
  activeSku: PropTypes.shape({}), // fetchProductByKey payload > sku_previews
  handleAddToCart: PropTypes.func.isRequired,
  setQuantity: PropTypes.func.isRequired,
  customizedInputsData: PropTypes.shape({}),
  savedPersonalization: PropTypes.shape({}),
  isPersonalizationRequired: PropTypes.bool,
  openPersonalizationModal: PropTypes.func,
  userContext: PropTypes.shape({}),
};

const mapStateToProps = (state) => ({
  customizedInputsData: state.personalization.customizedInputsData,
  savedPersonalization: state.personalization.savedPersonalization,
});

export default connect(mapStateToProps, null)(ProductOverview);
